import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Ticket, X } from 'lucide-react';

/**
 * Compact ticket popup anchored near the Home header "T" button.
 *
 * Shows the user's current-cycle ticket numbers (backend is the source of
 * truth — the numbers come straight from the API, never guessed on the client).
 * Closes on outside tap, Escape, or the close button. Kept small so it never
 * blocks the dashboard heavily.
 */
const TicketPopup = ({ isOpen, tickets = [], onClose }) => {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose?.();
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const safeTickets = Array.isArray(tickets) ? tickets : [];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={panelRef}
          data-testid="ticket-popup"
          initial={{ opacity: 0, y: -8, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.96 }}
          transition={{ type: 'spring', stiffness: 320, damping: 26 }}
          className="fixed right-4 top-[72px] z-[60] w-56 max-w-[80vw] glass-panel rounded-2xl border border-white/50 shadow-[0_18px_44px_rgba(147,51,234,0.22)] p-3"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Ticket className="w-4 h-4 text-[#16A34A]" strokeWidth={2.4} />
              <span className="text-sm font-semibold text-[#000000] font-['Outfit']">Your Tickets</span>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="ticket-popup-close"
              aria-label="Close tickets"
              className="p-1 rounded-full hover:bg-black/5 text-[#666666]"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {safeTickets.length === 0 ? (
            <p
              data-testid="ticket-popup-empty"
              className="text-sm text-[#666666] font-['DM_Sans'] py-2 text-center"
            >
              No tickets yet
            </p>
          ) : (
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {safeTickets.map((ticket) => (
                <div
                  key={ticket.ticketId || ticket.ticketNumber}
                  data-testid="ticket-popup-item"
                  className="flex items-center justify-center rounded-xl bg-white/70 border border-white/70 py-1.5"
                >
                  <span className="text-sm font-bold text-[#16A34A] font-['Outfit'] tracking-wide">
                    {ticket.label || `T : ${ticket.ticketNumber}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TicketPopup;
